/**
 * RecordOutcomeDialog — operator flags how a venue delivery actually
 * went. Append-only: each delivery attempt is its own row in
 * venue_delivery_outcomes. The next operator delivering to this venue
 * sees the recent history at the top of DeliverDialog and uses it to
 * pick format / KDM method / outreach approach without re-discovering
 * the venue's quirks.
 *
 * Outcome codes are kept in sync with the backend list (see
 * models/venue_outcome.py header comment).
 */
import React, { useState, useEffect } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions,
  Box, Typography, Button, IconButton, TextField,
  Chip, Alert, Checkbox, FormControlLabel,
} from '@mui/material';
import { Close as CloseIcon } from '@mui/icons-material';
import api from '../services/api';

// v10.17.19: outcomes that imply the digital delivery is no longer
// needed. When the operator picks one of these AND a share is in
// scope, we surface a "Also revoke this digital link" checkbox so the
// system stops tracking a download that isn't going to happen. The
// venue's gone silent for weeks and asked for a drive — the existing
// share row should reflect that, not keep counting views/downloads
// against a delivery method they've explicitly abandoned.
const OUTCOMES_THAT_KILL_DIGITAL = new Set([
  'pivoted_to_alt_delivery',
  'cancelled',
]);

// Outcomes grouped by tone so the picker reads top-to-bottom
// best→worst without making the operator scan every option.
// Outcome codes — kept in sync with backend models/venue_outcome.py.
// Order matters: this is the operator's top-to-bottom scan order.
// Pre-show (test) and post-show (screening) successes are split into
// their own codes as of v10.17.17 so the operator can confirm each
// distinct event. `success_clean` stays as the back-compat catch-all
// for cases where the operator just knows it worked but can't say
// which event they're confirming.
const OUTCOMES = [
  { value: 'test_successful',         label: 'Tested + confirmed (pre-show) — DCP ingested and played in test',  color: 'success' },
  { value: 'screening_successful',    label: 'Screening successful (post-show) — the actual showing went well',  color: 'success' },
  { value: 'success_clean',           label: 'Generic clean — they confirmed it worked (unspecified when)',      color: 'success' },
  { value: 'success_with_help',       label: 'Worked, but we walked them through it',     color: 'success' },
  { value: 'av_issue',                label: 'Played, but with AV issue (sync / level / picture / sound)', color: 'warning' },
  { value: 'kdm_filter_blocked',      label: 'KDM blocked by their corporate mail filter', color: 'warning' },
  { value: 'kdm_other_issue',         label: 'KDM had a different issue',                  color: 'warning' },
  { value: 'format_unknown',          label: "Didn't know what to do with the file format", color: 'error' },
  { value: 'download_failed',         label: 'Download failed / link broken',              color: 'error' },
  { value: 'unresponsive',            label: 'Ghosted — no confirmation back',             color: 'error' },
  { value: 'pivoted_to_alt_delivery', label: 'Gave up on digital — sent drive / FileMail / hand-off', color: 'default' },
  // v10.19.6: emergency fallback when KDM keys don't work at the venue
  // and we send an unencrypted MP4 instead. The screening goes forward
  // but the producer should know an unencrypted file was on the venue
  // network — render this outcome with a warning color so it stands
  // out in the venue + recipient history.
  { value: 'unencrypted_emergency_backup', label: '⚠ Unencrypted MP4 emergency backup (KDM keys failed at venue)', color: 'warning' },
  { value: 'cancelled',               label: 'Screening cancelled before delivery completed', color: 'default' },
  { value: 'other',                   label: 'Other (see notes)',                          color: 'default' },
];

// Outcomes that count as "the venue actually got it working." Surfaced
// elsewhere (Distribution timeline, dashboard) for color-coding.
export const SUCCESS_OUTCOMES = new Set([
  'test_successful',
  'screening_successful',
  'success_clean',
  'success_with_help',
]);

const FORMATS = [
  { value: 'zip',       label: '.zip' },
  { value: 'tar',       label: '.tar' },
  { value: 'folder',    label: 'folder' },
  { value: 'drive',     label: 'hard drive' },
  { value: 'quicktime', label: 'QuickTime / MP4' },
  { value: 'filemail',  label: 'FileMail / Cinesend / Aspera' },
  { value: 'other',     label: 'other' },
];

export default function RecordOutcomeDialog({
  open, onClose, venueId, venueLabel,
  // Optional: pre-fill format and FK references when invoked from a
  // specific share / DCP / KDM card — saves the operator typing.
  defaultFormat,
  dcpJobId,
  shareId,
  kdmRecordId,
  // True when there's an active (non-revoked) share for this delivery.
  // Surfaces the "also revoke" affordance only when there's something
  // for the operator to revoke; hidden on history-only outcome entries.
  shareIsActive,
  onSaved,
}) {
  const [format, setFormat] = useState(defaultFormat || '');
  const [outcome, setOutcome] = useState('');
  const [notes, setNotes] = useState('');
  const [revokeShare, setRevokeShare] = useState(false);
  const [error, setError] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open) {
      setFormat(defaultFormat || '');
      setOutcome('');
      setNotes('');
      setRevokeShare(false);
      setError(null);
    }
  }, [open, defaultFormat]);

  // Default-check the revoke box whenever the operator selects an
  // outcome that says "this digital link is dead." Operator can still
  // un-check before submitting (e.g. recording history retroactively).
  useEffect(() => {
    if (OUTCOMES_THAT_KILL_DIGITAL.has(outcome) && shareId && shareIsActive) {
      setRevokeShare(true);
    } else if (!OUTCOMES_THAT_KILL_DIGITAL.has(outcome)) {
      setRevokeShare(false);
    }
  }, [outcome, shareId, shareIsActive]);

  const submit = async () => {
    if (!outcome) { setError('Pick an outcome'); return; }
    setSaving(true);
    setError(null);
    try {
      // Two-step: revoke first (if requested), then record outcome.
      // If revoke fails we don't want a misleading "delivery was
      // abandoned" row in history pointing at a still-live share.
      if (revokeShare && shareId && shareIsActive) {
        await api.post(`/api/delivery/shares/${shareId}/revoke`);
      }
      await api.post(`/api/venues/${venueId}/outcomes`, {
        dcp_job_id: dcpJobId,
        share_id: shareId,
        kdm_record_id: kdmRecordId,
        format_used: format || null,
        outcome,
        notes: notes || null,
      });
      if (onSaved) onSaved({ revokedShare: revokeShare && shareIsActive });
      onClose();
    } catch (e) {
      setError(e.response?.data?.detail || e.message);
    } finally {
      setSaving(false);
    }
  };

  const showRevokeOption = OUTCOMES_THAT_KILL_DIGITAL.has(outcome)
    && !!shareId
    && shareIsActive;

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1, pr: 1 }}>
        <Box sx={{ flex: 1 }}>
          <Typography variant="h6" sx={{ fontSize: '1.05rem', fontWeight: 600 }}>How did this delivery go?</Typography>
          {venueLabel && (
            <Typography variant="caption" color="text.secondary">{venueLabel}</Typography>
          )}
        </Box>
        <IconButton size="small" onClick={onClose}><CloseIcon fontSize="small" /></IconButton>
      </DialogTitle>
      <DialogContent dividers>
        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

        <Typography variant="caption" sx={{ display: 'block', mb: 0.75, color: 'text.secondary', textTransform: 'uppercase', letterSpacing: 0.4, fontSize: '0.7rem' }}>
          What format did we send?
        </Typography>
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mb: 2 }}>
          {FORMATS.map((f) => (
            <Chip
              key={f.value}
              label={f.label}
              size="small"
              color={format === f.value ? 'primary' : 'default'}
              variant={format === f.value ? 'filled' : 'outlined'}
              onClick={() => setFormat(format === f.value ? '' : f.value)}
              sx={{ cursor: 'pointer' }}
            />
          ))}
        </Box>

        <Typography variant="caption" sx={{ display: 'block', mb: 0.75, color: 'text.secondary', textTransform: 'uppercase', letterSpacing: 0.4, fontSize: '0.7rem' }}>
          What happened?
        </Typography>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5, mb: 2 }}>
          {OUTCOMES.map((o) => (
            <Chip
              key={o.value}
              label={o.label}
              size="small"
              color={outcome === o.value ? o.color : 'default'}
              variant={outcome === o.value ? 'filled' : 'outlined'}
              onClick={() => setOutcome(outcome === o.value ? '' : o.value)}
              sx={{
                cursor: 'pointer',
                justifyContent: 'flex-start',
                width: 'fit-content',
                maxWidth: '100%',
                '& .MuiChip-label': { whiteSpace: 'normal', textAlign: 'left', py: 0.4 },
                height: 'auto',
              }}
            />
          ))}
        </Box>

        {/* Revoke-on-pivot affordance. Only surfaces when the chosen
            outcome implies the digital is no longer needed (pivoted to
            alternate delivery, or screening cancelled) AND there's an
            active share to revoke. Default-checked because the typical
            case is "venue asked for a drive 6 days out, stop tracking
            this download." Operator can un-check to record history
            retroactively without touching the share row. */}
        {showRevokeOption && (
          <Box
            sx={{
              p: 1.25, mb: 2,
              borderRadius: 1,
              border: '1px solid',
              borderColor: 'warning.main',
              bgcolor: 'rgba(237,108,2,0.06)',
            }}
          >
            <FormControlLabel
              control={
                <Checkbox
                  size="small"
                  checked={revokeShare}
                  onChange={(e) => setRevokeShare(e.target.checked)}
                />
              }
              label={
                <Typography variant="body2" sx={{ fontSize: '0.82rem' }}>
                  Also revoke the digital delivery link
                </Typography>
              }
            />
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ display: 'block', mt: 0.25, ml: 4, fontSize: '0.72rem' }}
            >
              The recipient's download page will return "Link Revoked"
              and view/download counters stop accruing. Their email link
              still exists; revoking just kills the page behind it.
            </Typography>
          </Box>
        )}

        <TextField
          label="Notes (visible to the next operator)"
          fullWidth multiline minRows={3} maxRows={8}
          placeholder="e.g. Tech contact prefers phone over email; KDM filter strips .xml — use ZIP attach next time"
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
        />
      </DialogContent>
      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button onClick={onClose}>Cancel</Button>
        <Box sx={{ flex: 1 }} />
        <Button
          variant="contained"
          onClick={submit}
          disabled={!outcome || saving}
        >
          {saving ? 'Saving…' : 'Record outcome'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
